import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/axios';
import {rateFromScaled} from '../../components/utils/UnitConversion.utils';

export type Currency =
    | 'USD'
    | 'EUR'
    | 'GBP'
    | 'JPY'
    | 'AUD'
    | 'CAD'
    | 'CHF'
    | 'CNY'
    | 'SEK'
    | 'NZD'
    | 'BRL'
    | 'CZK'
    | 'HRN'
    | 'IDR'
    | 'INR'
    | 'KRW'
    | 'NOK'
    | 'PLN'
    | 'RUB'
    | 'THB'
    | 'TRY';

export interface CurrencyState {
  currency: Currency;
  symbol: string;
  rate: number;
  loading: boolean;
}

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: '$',
  CAD: '$',
  CHF: 'CHF',
  CNY: '¥',
  SEK: 'kr',
  NZD: '$',
  BRL: 'R$',
  CZK: 'Kč',
  HRN: '₴',
  IDR: 'Rp',
  INR: '₹',
  KRW: '₩',
  NOK: 'kr',
  PLN: 'zł',
  RUB: '₽',
  THB: '฿',
  TRY: '₺',
};

const getCurrencyFromBrowser = (): Currency => {
  try {
    const currency = Intl.NumberFormat().resolvedOptions().currency;
    return currency?.toUpperCase() as Currency;
  } catch {
    return 'USD';
  }
};

export const fetchCurrencyRate = createAsyncThunk<number, Currency>(
    'currency/fetchRate',
    async (currency) => {
      const res = await api.get(`/currency/${currency}`);
      return rateFromScaled(res.data.rateScaled);
    }
);

const resolvedCurrency =
    (localStorage.getItem('currency') as Currency)
    || getCurrencyFromBrowser()
    || 'USD';

const initialState: CurrencyState = {
  currency: resolvedCurrency,
  symbol: CURRENCY_SYMBOLS[resolvedCurrency],
  rate: 1,
  loading: false,
};

const currencySlice = createSlice({
  name: 'currency',
  initialState,
  reducers: {
    setCurrency: (state, action: PayloadAction<Currency>) => {
      state.currency = action.payload;
      state.symbol = CURRENCY_SYMBOLS[action.payload];
      localStorage.setItem('currency', action.payload);
    },
    setRate: (state, action: PayloadAction<number>) => {
      state.rate = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchCurrencyRate.pending, (state) => { state.loading = true; })
    .addCase(fetchCurrencyRate.fulfilled, (state, action) => {
      state.rate = action.payload;
      state.loading = false;
    })
    .addCase(fetchCurrencyRate.rejected, (state) => { state.loading = false; });
  },
});

export const { setCurrency, setRate, setLoading } = currencySlice.actions;
export default currencySlice.reducer;
